import { createTheme, type Theme, type ThemeOptions } from '@mui/material/styles';

import { aurora, ice, midnight, semantic } from '../tokens/colors.js';
import { fontFamily } from '../tokens/typography.js';

/**
 * MUI theme adapter for the Norvix Aurora v2 design system.
 * Re-uses the same hex values committed in packages/ui/tokens/*.json.
 */
export function createNorvixMuiTheme(overrides: ThemeOptions = {}): Theme {
  return createTheme({
    palette: {
      mode: 'dark',
      primary:   { main: aurora[500], light: aurora[300], dark: aurora[600], contrastText: midnight[950] },
      secondary: { main: ice[300],    light: ice[100],    dark: ice[500],    contrastText: midnight[950] },
      success:   { main: semantic.success, contrastText: midnight[950] },
      warning:   { main: semantic.warning, contrastText: midnight[950] },
      error:     { main: semantic.error,   contrastText: '#ffffff' },
      info:      { main: semantic.info,    contrastText: midnight[950] },
      background: {
        default: midnight[900],
        paper:   midnight[800],
      },
      text: {
        primary:   ice[100],
        secondary: ice[300],
        disabled:  ice[500],
      },
      divider: 'rgba(255, 255, 255, 0.10)',
    },
    shape: { borderRadius: 12 },
    typography: {
      fontFamily: fontFamily.sans,
      h1: { fontFamily: fontFamily.display, fontWeight: 800, fontSize: '3.5rem', lineHeight: 1.10, letterSpacing: '-0.02em' },
      h2: { fontFamily: fontFamily.display, fontWeight: 800, fontSize: '2.5rem', lineHeight: 1.15, letterSpacing: '-0.02em' },
      h3: { fontFamily: fontFamily.display, fontWeight: 700, fontSize: '2rem',   lineHeight: 1.20, letterSpacing: '-0.01em' },
      h4: { fontWeight: 700, fontSize: '1.5rem',  lineHeight: 1.25, letterSpacing: '-0.01em' },
      h5: { fontWeight: 600, fontSize: '1.25rem', lineHeight: 1.30 },
      h6: { fontWeight: 600, fontSize: '1rem',    lineHeight: 1.40 },
      body1: { fontSize: '1rem',    lineHeight: 1.50 },
      body2: { fontSize: '0.875rem',lineHeight: 1.45 },
      caption: { fontSize: '0.75rem', lineHeight: 1.40 },
      overline: { fontSize: '0.75rem', lineHeight: 1.40, letterSpacing: '0.24em', fontWeight: 600, textTransform: 'uppercase' },
      button: { textTransform: 'none', fontWeight: 600 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          // Mirrors @norvix/ui base.css. Viewport-anchored so a long page —
          // a review workspace, a full queue — cannot scroll past the
          // decoration and expose bare canvas.
          html: { minHeight: '100%', backgroundColor: midnight[900] },
          body: {
            minHeight: '100%',
            backgroundColor: midnight[900],
            backgroundImage: `radial-gradient(1200px 800px at 20% -10%, rgba(0, 201, 252, 0.08), transparent 60%),
                              radial-gradient(900px 600px at 100% 100%, rgba(28, 58, 106, 0.25), transparent 60%)`,
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'fixed',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: midnight[950],
            backgroundImage: 'none',
            borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
            boxShadow: 'none',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: midnight[950],
            backgroundImage: 'none',
            borderRight: '1px solid rgba(255, 255, 255, 0.06)',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            border: '1px solid rgba(255, 255, 255, 0.06)',
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 12,
            fontWeight: 600,
            fontSize: '0.9375rem',
            paddingInline: 24,
            paddingBlock: 10,
            transition: 'box-shadow 200ms cubic-bezier(0.4, 0.0, 0.2, 1.0), background-color 200ms cubic-bezier(0.4, 0.0, 0.2, 1.0), border-color 200ms cubic-bezier(0.4, 0.0, 0.2, 1.0)',
          },
          sizeLarge: { paddingBlock: 14, paddingInline: 28 },
          containedPrimary: {
            // Figma btn-primary: aurora fill + resting glow, brighter glow on hover.
            boxShadow: 'var(--shadow-btn-primary)',
            '&:hover': { backgroundColor: aurora[400], boxShadow: 'var(--shadow-btn-primary-hover)' },
          },
          outlinedPrimary: {
            // Figma btn-secondary: aurora hairline on a midnight surface.
            borderColor: aurora[500],
            '&:hover': { borderColor: aurora[400], backgroundColor: 'rgba(0, 201, 252, 0.08)' },
          },
          textPrimary: {
            // Figma btn-ghost.
            color: ice[100],
            border: '1px solid rgba(255, 255, 255, 0.12)',
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.04)', borderColor: 'rgba(255, 255, 255, 0.20)' },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            backgroundColor: midnight[700],
            transition: 'box-shadow 150ms cubic-bezier(0.4, 0.0, 0.2, 1.0)',
            '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.12)' },
            '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.22)' },
            // Figma input-focus: aurora 2px border + aurora glow.
            '&.Mui-focused': { boxShadow: 'var(--shadow-input-focus)' },
            '&.Mui-focused fieldset': { borderColor: aurora[500], borderWidth: 2 },
            // Figma input-error.
            '&.Mui-error fieldset': { borderColor: semantic.error, borderWidth: 2 },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          // Figma chips: pill radius, tinted fill (≈15% of the chip colour) + colour label/border.
          root: { borderRadius: 999, fontWeight: 500, fontSize: 13, height: 26 },
          label: { paddingLeft: 12, paddingRight: 12 },
          outlined: { backgroundColor: 'color-mix(in srgb, currentColor 14%, transparent)' },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderBottom: '1px solid rgba(255, 255, 255, 0.06)' },
          head: { color: ice[500], fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.75rem' },
        },
      },
    },
    ...overrides,
  });
}
